import { useEffect, useId, useState, type JSX, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { EDGE_GAP, outside, useAnchoredPanel } from './anchored';
import { Icon } from './Icon';

/**
 * Пояснение, спрятанное под кнопку.
 *
 * Заведено по итогам ручного прогона фазы 9а на телефоне. Шапки разделов
 * набирались на широком экране, где рядом с заголовком помещается ещё
 * и строка-другая пояснений: «один на площадку», «автообновление
 * не настроено», «0 потоков / 16», «substream». На 360 px эти строки
 * переносятся внутри себя, распирают шапку по высоте и выдавливают
 * за экран то, ради чего в шапку и смотрят, — кнопки.
 *
 * Выбрасывать пояснения нельзя: они отвечают на вопросы, которые задают
 * не каждый день, но задают. Поэтому они не исчезают, а сворачиваются
 * в один значок: место в строке он занимает постоянное, а разворачивается
 * по нажатию — и ровно тогда, когда спросили.
 *
 * Панель держится в пределах экрана и не обрезается чужим `overflow`:
 * этим занимается `useAnchoredPanel` — она же под списком языков.
 */
export function InfoNote({
  label,
  summary,
  tone = 'muted',
  children,
}: {
  /** Чем кнопка называется вслух: подсказка и подпись для скринридера. */
  label: string;
  /**
   * Короткая подпись рядом со значком.
   *
   * Нужна там, где само наличие пояснения — уже сообщение: «агента нет»
   * должно быть видно без нажатия, иначе предупреждение перестаёт
   * предупреждать. Где пояснение справочное, подписи нет.
   */
  summary?: string;
  /** `warn` — когда за пояснением стоит неполадка, а не справка. */
  tone?: 'muted' | 'warn';
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const { anchor, panel, style } = useAnchoredPanel<HTMLDivElement, HTMLDivElement>(open);

  /*
    Закрытие по Escape и по нажатию мимо — то же, что у шторки. Пояснение
    открывают, чтобы прочитать, и закрывать его должно всё, что означает
    «прочитал»: и клавиша, и палец в стороне.
  */
  useEffect(() => {
    if (!open) {
      return;
    }

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const onOutside = (event: PointerEvent): void => {
      if (outside(event.target, anchor, panel)) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKey);
    // На фазе захвата: иначе нажатие по кнопке внутри чужого меню успело бы
    // закрыть панель раньше, чем сработало бы само нажатие.
    window.addEventListener('pointerdown', onOutside, true);

    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onOutside, true);
    };
  }, [open, anchor, panel]);

  return (
    <div ref={anchor} className="relative flex-none">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className={`flex h-6.5 items-center gap-1 rounded-sm px-1.5 text-xs whitespace-nowrap ${
          tone === 'warn'
            ? 'border border-warn text-warn'
            : 'border border-transparent text-muted hover:text-fg'
        }`}
      >
        <Icon name="info" />
        {summary === undefined ? null : <span>{summary}</span>}
      </button>

      {open
        ? createPortal(
            <div
              id={panelId}
              ref={panel}
              /*
                Ширина считается от экрана, а не от содержимого: на 360 px
                панель в 320 px с полями уже упирается в края.

                Начертание задаётся явно: пояснение открывают из мест
                с собственной типографикой — заголовок списка слоёв набран
                моноширинными прописными, — и наследование превращало текст
                в полосу капса.
              */
              style={{ ...style, width: panelWidth() }}
              className="z-50 rounded-sm border border-line-strong bg-raised p-3 font-sans text-xs leading-[1.5] tracking-normal text-muted normal-case shadow-lg"
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * Ширина панели.
 *
 * Двадцать рем — столько, чтобы два-три предложения не растягивались в нитку;
 * на узком экране — по нему, оставляя поля. Считается числом, а не классом,
 * потому что от неё же зависит сдвиг: класс браузер знает, а мы нет.
 */
function panelWidth(): number {
  const room = typeof window === 'undefined' ? 320 : window.innerWidth;

  return Math.min(320, room - EDGE_GAP * 2);
}
